import type { APIError, ImageType } from '@vizit/api';
import { FEATURE_FLAGS } from '@vizit/constants';
import { useCustomBenchmarksAvailability, useFeatureFlag } from '@vizit/feature-flags';
import { CollectionsDialog } from '@vizit/organisms';
import * as React from 'react';
import { useCollectionNewDialog, useProductCategoryList } from './collection-new-dialog.hooks';
import { useCustomBenchmarkOptions, useScorableRetailers } from './collection-new-dialog.hooks';

interface CollectionNewDialogProps {
  /**
   * The ID of the portfolio to use for the collection
   *
   * @optional
   * @type {string}
   */
  workspaceId?: string;
  /**
   * The ID of the organization to use for the collection
   *
   * @optional
   * @type {string}
   */
  organizationId?: string;
  /**
   * Whether to use an icon for the trigger button
   *
   * @optional
   * @type {boolean}
   */
  useIcon?: boolean;
  /**
   * Whether the dialog is open
   *
   * @optional
   * @type {boolean}
   */
  isOpen?: boolean;
  /**
   * Whether to hide the trigger button
   * Set to true when controlled externally (e.g., by CallToActionCard)
   *
   * @optional
   * @type {boolean}
   */
  hideTrigger?: boolean;
  /**
   * The function to call when the collection is successfully created
   *
   * @param collectionId - The ID of the created collection
   */
  onSuccessfulCreate: (collectionId: string) => void;
  /**
   * The function to call when the authentication error occurs
   *
   * @param error - The error that occurred
   */
  onAuthenticationError: (error: APIError) => void;
  /**
   * Callback function when dialog open state changes
   *
   * @optional
   * @param isOpen - Whether the dialog is open
   */
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * CollectionNewDialog component
 * Provides a dialog interface for creating new collections with product category selection
 *
 * @param props - Component props
 * @returns CollectionNewDialog component
 *
 * @example
 * <CollectionNewDialog
 *   workspaceId="portfolio-123"
 *   organizationId="org-123"
 *   onSuccessfulCreate={(id) => console.log('Created:', id)}
 *   onAuthenticationError={(error) => console.error(error)}
 * />
 */
function CollectionNewDialog(props: CollectionNewDialogProps) {
  const {
    workspaceId,
    organizationId,
    useIcon = false,
    isOpen,
    hideTrigger = false,
    onSuccessfulCreate,
    onAuthenticationError,
    onOpenChange,
    ...other
  } = props;

  const productCategoryComboBoxProps = useProductCategoryList({
    organizationId,
    workspaceId,
    onAuthenticationError,
  });

  const { submissionErrorMessage, onSubmit } = useCollectionNewDialog({
    workspaceId,
    organizationId,
    onSuccessfulCreate,
    onAuthenticationError,
  });
  const hideHeroOption = useFeatureFlag(FEATURE_FLAGS.HIDE_HERO_IN_IMAGE_FLOWS, false);
  const benchmarksAvailability = useCustomBenchmarksAvailability(workspaceId);
  // The field shows while the API check runs, as loading, and goes away when benchmarks are off here.
  const showCustomBenchmarks = benchmarksAvailability.isEnabled || benchmarksAvailability.isChecking;
  const customBenchmarks = useCustomBenchmarkOptions({ workspaceId, enabled: benchmarksAvailability.isEnabled });
  const [selection, setSelection] = React.useState<{ category: string; imageType?: ImageType }>({ category: '' });
  const scorableRetailers = useScorableRetailers({
    workspaceId,
    ...selection,
    enabled: benchmarksAvailability.isEnabled,
  });

  return (
    <CollectionsDialog
      submissionErrorMessage={submissionErrorMessage}
      productCategoryComboBoxProps={productCategoryComboBoxProps}
      useIcon={useIcon}
      isOpen={isOpen}
      hideTrigger={hideTrigger}
      hideHeroOption={hideHeroOption}
      showBenchmarkField={showCustomBenchmarks}
      customBenchmarkOptions={customBenchmarks.options}
      isBenchmarkOptionsLoading={
        benchmarksAvailability.isChecking || (showCustomBenchmarks && customBenchmarks.isLoading)
      }
      benchmarkOptionsErrorMessage={customBenchmarks.errorMessage}
      scorableRetailers={scorableRetailers}
      onCategoryOrImageTypeChange={setSelection}
      onSubmit={onSubmit}
      onOpenChange={onOpenChange}
      data-testid="library-collections-create-collection"
      {...other}
    />
  );
}

export { CollectionNewDialog };
